import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { decodeToken } from "react-jwt";

import validateForm from "../../../utils/FormValidation";
import validationRules from "../../../assets/validation/rules";
import { loginStart, loginSuccess, loginFailure } from "../../../redux/slices/authSlice";
import AuthRestAPI from "../../../restAPI/AuthRestAPI";
import { AESEncryption, AESDecryption } from "../../../utils/Aes";
import { getApiErrorMessage } from "../../../utils/apiError";
import AuthPageAside from "./AuthPageAside";
import AuthInsightCards from "./AuthInsightCards";
import moguLogo from "../../../assets/logo/mogu-logo-cover.png";
import "./AuthFlow.css";

const Brand = () => (
  <div className="brand">
    <span className="brand-mark brand-mark--login-cover">
      <img className="brand-mark-image-cover" src={moguLogo} alt="MOGU logo" />
    </span>
  </div>
);

const MoguLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({ username: "", password: "" });
  const [errors, setErrors] = useState({});
  const [isRememberMe, setIsRememberMe] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    if (localStorage.getItem("username") !== null && localStorage.getItem("password") !== null) {
      setPayload({
        username: AESDecryption(localStorage.getItem("username")),
        password: AESDecryption(localStorage.getItem("password")),
      });
      setIsRememberMe(true);
    }
  }, []);

  const validateFormData = (data) => {
    const validationResult = validateForm(data, validationRules);
    if (!validationResult.isValid) {
      const err = validationResult.errors;
      const newErrors = {};
      Object.keys(err).forEach((value) => {
        newErrors[value] = err[value];
      });
      setErrors((prev) => ({ ...prev, ...newErrors }));
    } else {
      const keyData = Object.keys(data)[0];
      setErrors((prev) => ({ ...prev, [keyData]: "" }));
    }
  };

  const handleLogin = (e) => {
    e?.preventDefault();
    setIsButtonDisabled(true);

    const validateData = {
      username: payload.username,
      loginPasswordWhenLogin: payload.password,
    };

    const validationResult = validateForm(validateData, validationRules);
    if (!validationResult.isValid) {
      const err = validationResult.errors;
      const newErrors = {};
      Object.keys(err).forEach((value) => {
        newErrors[value] = err[value];
      });
      setErrors(newErrors);
      setIsButtonDisabled(false);
      return;
    }

    setErrors({});
    dispatch(loginStart());
    setLoading(true);

    if (isRememberMe) {
      localStorage.setItem("username", AESEncryption(payload.username));
      localStorage.setItem("password", AESEncryption(payload.password));
    } else {
      localStorage.removeItem("username");
      localStorage.removeItem("password");
    }

    AuthRestAPI.login(payload)
      .then((res) => {
        const decodedTokenValue = decodeToken(res.token);

        if (decodedTokenValue?.isFirstTime) {
          setLoading(false);
          navigate("/firsttimeresetpassword/:tokenId?" + res.token);
          return;
        }

        const successPayload = {
          token: res.token,
          userId: decodedTokenValue.userId,
          firstName: decodedTokenValue.firstName,
          email: decodedTokenValue.email,
          role: decodedTokenValue.role,
        };

        localStorage.setItem("token", AESEncryption(res.token));
        dispatch(loginSuccess(successPayload));
        navigate("/dashboard");
      })
      .catch((err) => {
        const message = getApiErrorMessage(err, "Login failed. Please try again.");
        dispatch(loginFailure(err.response?.data ?? { message }));
        setErrors({ loginPasswordWhenLogin: message });
        setTimeout(() => setIsButtonDisabled(false), 6000);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="shell">
      <section className="left">
        <Brand />
        <div className="form-wrap">
          <div className="accent-bar"></div>
          <h1>
            Sign in to your <em>account</em>
          </h1>
          <p className="subtitle">
            Upload your invoices for LHDN validation, view your submissions, and manage your master data.
          </p>

          <form onSubmit={handleLogin}>
            <div className="field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="email"
                placeholder="you@company.com"
                autoComplete="username"
                value={payload.username}
                onChange={(e) => {
                  const v = e.target.value;
                  validateFormData({ username: v });
                  setPayload((prev) => ({ ...prev, username: v }));
                }}
                required
              />
              {errors.username && <span className="field-error">{errors.username}</span>}
            </div>

            <div className="field">
              <label htmlFor="pw">Password</label>
              <input
                id="pw"
                className="has-icon-right"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••••"
                autoComplete="current-password"
                value={payload.password}
                onChange={(e) => setPayload((prev) => ({ ...prev, password: e.target.value }))}
                required
              />
              <button
                type="button"
                className="toggle-eye"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((s) => !s)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
              {errors.loginPasswordWhenLogin && <span className="field-error">{errors.loginPasswordWhenLogin}</span>}
            </div>

            <div className="row">
              <label className="check">
                <input type="checkbox" checked={isRememberMe} onChange={(e) => setIsRememberMe(e.target.checked)} />
                Keep me signed in
              </label>
              <button type="button" className="link-button" onClick={() => navigate("/forgot-password")}>
                Forgot password?
              </button>
            </div>

            <button className="btn" type="submit" disabled={isButtonDisabled || loading}>
              {loading ? "Signing in..." : "Sign in"}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </form>
          <div className="foot">
            <span>© {new Date().getFullYear()} MOGU Sdn Bhd</span>
          </div>
        </div>
      </section>

      <AuthPageAside>
        <h2 className="login-right-title">
          The command center for <em>compliant</em> e-invoicing in Malaysia
        </h2>
        <div className="login-motion-divider" aria-hidden="true">
          <span className="login-motion-divider__line" />
          <span className="login-motion-divider__orb" />
          <span className="login-motion-divider__trail" />
        </div>
        <AuthInsightCards />
      </AuthPageAside>
    </div>
  );
};

export default MoguLogin;
